<template>
    <div class="flex-1 bg-gray-50 p-6 flex flex-col h-full">
        <!-- 页面标题和操作栏 -->
        <div class="flex justify-between items-center mb-6 flex-shrink-0">
            <div class="flex items-center space-x-6">
                <h1 class="text-3xl font-bold text-gray-900">当前知识库文档</h1>
                                 <!-- 上传按钮区域 -->
                 <div class="flex space-x-3">
                     <button 
                         @click="openUploadModal()"
                         class="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg transition-all duration-200 flex items-center space-x-2 hover:scale-105"
                     >
                         <i class="fas fa-file-upload"></i>
                         <span>文档上传</span>
                     </button>
                     
                     <!-- 批量删除按钮 -->
                     <button 
                         @click="batchDeleteDocuments"
                         :disabled="selectedDocuments.length === 0"
                         class="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg shadow-lg transition-all duration-200 flex items-center space-x-2 hover:scale-105 disabled:cursor-not-allowed"
                     >
                         <i class="fas fa-trash"></i>
                         <span>批量删除 ({{ selectedDocuments.length }})</span>
                     </button>
                 </div>
            </div>
            <div class="flex items-center space-x-4">
                <!-- 每页条数选择 -->
                <div class="flex items-center space-x-3">
                    <span class="text-sm font-medium text-gray-700">每页显示</span>
                    <div class="flex bg-white border border-gray-300 rounded-lg p-1 shadow-sm">
                        <button 
                            @click="changePageSize(10)"
                            :class="[
                                'px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200',
                                pageSize === 10 
                                    ? 'bg-blue-500 text-white shadow-sm' 
                                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                            ]"
                        >
                            10
                        </button>
                        <button 
                            @click="changePageSize(20)"
                            :class="[
                                'px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200',
                                pageSize === 20 
                                    ? 'bg-blue-500 text-white shadow-sm' 
                                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                            ]"
                        >
                            20
                        </button>
                        <button 
                            @click="changePageSize(30)"
                            :class="[
                                'px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200',
                                pageSize === 30 
                                    ? 'bg-blue-500 text-white shadow-sm' 
                                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                            ]"
                        >
                            30
                        </button>
                    </div>
                </div>
                
                <!-- 排序下拉菜单 -->
                <div class="relative">
                    <select 
                        v-model="sortBy" 
                        @change="handleSort"
                        class="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        <option value="recent">最近上传</option>
                        <option value="name">按名称</option>
                        <option value="size">按大小</option>
                        <option value="type">按类型</option>
                    </select>
                    <div class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <i class="fas fa-chevron-down text-gray-400"></i>
                    </div>
                </div>
                
                <!-- 视图切换按钮 -->
                <div class="flex bg-white border border-gray-300 rounded-lg p-1">
                    <button 
                        @click="viewMode = 'grid'"
                        :class="[
                            'p-2 rounded-md transition-colors duration-200',
                            viewMode === 'grid' 
                                ? 'bg-blue-500 text-white' 
                                : 'text-gray-600 hover:text-gray-800'
                        ]"
                        title="网格视图"
                    >
                        <i class="fas fa-th"></i>
                    </button>
                    <button 
                        @click="viewMode = 'list'"
                        :class="[
                            'p-2 rounded-md transition-colors duration-200',
                            viewMode === 'list' 
                                ? 'bg-blue-500 text-white' 
                                : 'text-gray-600 hover:text-gray-800'
                        ]"
                        title="列表视图"
                    >
                        <i class="fas fa-list"></i>
                    </button>
                </div>
            </div>
        </div>

        <!-- 删除规则提示 -->
        <div class="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div class="flex items-center space-x-3">
                <i class="fas fa-exclamation-triangle text-yellow-600 text-lg"></i>
                <div>
                    <p class="text-yellow-800 font-medium">删除规则说明</p>
                    <p class="text-yellow-700 text-sm">当文件正在解析或已解析完成时不可删除，只有未解析的文件才能删除，如需删除请联系管理员！</p>
                </div>
            </div>
        </div>

        <!-- 文档列表 -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 flex-1 flex flex-col min-h-0">
            <!-- 加载状态 -->
            <div v-if="loading" class="p-8 text-center flex-1 flex items-center justify-center">
                <div class="inline-flex items-center space-x-2">
                    <i class="fas fa-spinner fa-spin text-blue-500 text-xl"></i>
                    <span class="text-gray-600">正在加载文档列表...</span>
                </div>
            </div>
            
            <!-- 空状态 -->
            <div v-else-if="documents.length === 0" class="p-8 text-center flex-1 flex items-center justify-center">
                <div>
                    <i class="fas fa-folder-open text-gray-400 text-4xl mb-4"></i>
                    <p class="text-gray-600 text-lg">暂无文档</p>
                    <p class="text-gray-500 text-sm mt-2">点击上方按钮上传您的第一个文档</p>
                </div>
            </div>
            
            <!-- 列表视图 -->
            <div v-else-if="viewMode === 'list'" class="divide-y divide-gray-200 flex-1 overflow-y-auto min-h-0">
                                 <!-- 表头 -->
                 <div class="grid grid-cols-12 gap-4 p-4 bg-gray-50 text-sm font-medium text-gray-700 border-b border-gray-200">
                     <div class="col-span-1 text-center">
                         <input 
                             type="checkbox" 
                             :checked="isAllSelected" 
                             @change="toggleSelectAll"
                             class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                         />
                     </div>
                     <div class="col-span-1 text-center">序号</div>
                     <div class="col-span-1 text-center">类型</div>
                     <div class="col-span-2">文件名</div>
                     <div class="col-span-2">上传时间</div>
                     <div class="col-span-2">文件大小</div>
                     <div class="col-span-2">是否已解析</div>
                     <div class="col-span-1 text-center">操作</div>
                 </div>
                
                                 <!-- 文档行 -->
                 <div 
                     v-for="(doc, index) in documents" 
                     :key="doc.id"
                     class="grid grid-cols-12 gap-4 p-4 hover:bg-gray-50 transition-colors duration-200 items-center"
                 >
                     <!-- 复选框 -->
                     <div class="col-span-1 text-center">
                         <input 
                             type="checkbox" 
                             :checked="selectedDocuments.includes(doc.id)" 
                             @change="toggleDocumentSelection(doc.id)"
                             :disabled="doc.progress > 0"
                             class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                         />
                     </div>
                     
                     <!-- 序号 -->
                     <div class="col-span-1 text-sm text-gray-900 font-bold text-center">
                         {{ (currentPage - 1) * pageSize + index + 1 }}
                     </div>
                     
                     <!-- 文件类型图标 -->
                     <div class="col-span-1 flex items-center justify-center">
                         <div class="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                              :class="getFileTypeClass(doc.type)">
                             {{ getFileTypeIcon(doc.type) }}
                         </div>
                     </div>
                     
                     <!-- 文件信息 -->
                     <div class="col-span-2">
                         <h3 class="text-lg font-medium text-gray-900 truncate" :title="doc.name">{{ doc.name }}</h3>
                     </div>
                    
                    <!-- 上传时间 -->
                    <div class="col-span-2 text-sm text-gray-500">
                        {{ formatDate(doc.create_time) }}
                    </div>
                    
                    <!-- 文件大小 -->
                    <div class="col-span-2 text-sm text-gray-500">
                        {{ formatFileSize(doc.size) }}
                    </div>
                    
                                         <!-- 是否已解析状态 -->
                     <div class="col-span-2">
                         <div class="flex items-center space-x-2">
                             <span class="text-sm font-medium text-gray-700">是否已解析:</span>
                             <div class="flex items-center">
                                 <div 
                                     :class="[
                                         'w-4 h-4 rounded-full border-2 transition-colors duration-200',
                                         doc.progress === 1
                                             ? 'bg-green-500 border-green-500' 
                                             : doc.progress > 0
                                             ? 'bg-blue-500 border-blue-500'
                                             : 'bg-gray-300 border-gray-400'
                                     ]"
                                 ></div>
                                 <span 
                                     :class="[
                                         'ml-2 text-sm font-medium',
                                         doc.progress === 1 ? 'text-green-600' : doc.progress > 0 ? 'text-blue-600' : 'text-gray-500'
                                     ]"
                                 >
                                     {{ doc.progress === 1 ? '已解析' : doc.progress > 0 ? `${(doc.progress * 100).toFixed(2)}%` : '未解析' }}
                                 </span>
                             </div>
                         </div>
                     </div>
                    
                    <!-- 操作按钮 -->
                    <div class="col-span-1 flex justify-center space-x-2">
                        <button 
                            @click="downloadDocument(doc)"
                            :disabled="doc.downloading"
                            :class="[
                                'p-2 rounded-lg transition-colors duration-200',
                                doc.downloading 
                                    ? 'text-gray-400 cursor-not-allowed' 
                                    : 'text-gray-500 hover:text-green-600 hover:bg-green-50'
                                ]"
                            :title="doc.downloading ? '下载中...' : '下载'"
                        >
                            <i v-if="doc.downloading" class="fas fa-spinner fa-spin"></i>
                            <i v-else class="fas fa-download"></i>
                        </button>
                        <button 
                            @click="deleteDocument(doc)"
                            :disabled="doc.progress > 0"
                            :class="[
                                'p-2 rounded-lg transition-colors duration-200',
                                doc.progress > 0
                                    ? 'text-gray-300 cursor-not-allowed bg-gray-100' 
                                    : 'text-gray-500 hover:text-red-600 hover:bg-red-50'
                            ]"
                            :title="doc.progress > 0 ? '解析中或已解析的文件不可删除' : '删除'"
                        >
                            <i class="fas fa-trash"></i>
                        </button>
                    </div>
                </div>
            </div>

            <!-- 网格视图 -->
            <div v-else class="flex-1 overflow-y-auto min-h-0">
                <div class="p-6">
                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div 
                            v-for="doc in documents" 
                            :key="doc.id"
                            class="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors duration-200 border border-gray-200"
                        >
                            <!-- 文件类型图标 -->
                            <div class="w-16 h-16 rounded-lg flex items-center justify-center text-white font-bold text-lg mx-auto mb-3"
                                 :class="getFileTypeClass(doc.type)">
                                {{ getFileTypeIcon(doc.type) }}
                            </div>
                            
                            <!-- 文件信息 -->
                            <div class="text-center mb-4">
                                <h3 class="text-lg font-medium text-gray-900 mb-2 line-clamp-2">{{ doc.name }}</h3>
                                <div class="text-sm text-gray-500">
                                    上传时间: {{ formatDate(doc.create_time) }}
                                </div>
                                <div class="text-sm text-gray-500">{{ formatFileSize(doc.size) }}</div>
                            </div>
                            
                                                         <!-- 是否已解析状态 -->
                             <div class="flex justify-center items-center space-x-2 mb-3">
                                 <span class="text-sm font-medium text-gray-700">是否已解析:</span>
                                 <div class="flex items-center">
                                     <div 
                                         :class="[
                                             'w-4 h-4 rounded-full border-2 transition-colors duration-200',
                                             doc.progress === 1
                                                 ? 'bg-green-500 border-green-500' 
                                                 : doc.progress > 0
                                                 ? 'bg-blue-500 border-blue-500'
                                                 : 'bg-gray-300 border-gray-400'
                                         ]"
                                     ></div>
                                     <span 
                                         :class="[
                                             'ml-2 text-sm font-medium',
                                             doc.progress === 1 ? 'text-green-600' : doc.progress > 0 ? 'text-blue-600' : 'text-gray-500'
                                         ]"
                                     >
                                         {{ doc.progress === 1 ? '已解析' : doc.progress > 0 ? `${(doc.progress * 100).toFixed(2)}%` : '未解析' }}
                                     </span>
                                 </div>
                             </div>
                            
                            <!-- 操作按钮 -->
                            <div class="flex justify-center space-x-2">
                                <button 
                                    @click="downloadDocument(doc)"
                                    :disabled="doc.downloading"
                                    :class="[
                                        'p-2 rounded-lg transition-colors duration-200',
                                        doc.downloading 
                                            ? 'text-gray-400 cursor-not-allowed' 
                                            : 'text-gray-500 hover:text-green-600 hover:bg-green-50'
                                        ]"
                                    :title="doc.downloading ? '下载中...' : '下载'"
                                >
                                    <i v-if="doc.downloading" class="fas fa-spinner fa-spin"></i>
                                    <i v-else class="fas fa-download"></i>
                                </button>
                                                                 <button 
                                     @click="deleteDocument(doc)"
                                     :disabled="doc.progress > 0"
                                     :class="[
                                         'p-2 rounded-lg transition-colors duration-200',
                                         doc.progress > 0
                                             ? 'text-gray-300 cursor-not-allowed bg-gray-100' 
                                             : 'text-gray-500 hover:text-red-600 hover:bg-red-50'
                                     ]"
                                     :title="doc.progress > 0 ? '解析中或已解析的文件不可删除' : '删除'"
                                 >
                                     <i class="fas fa-trash"></i>
                                 </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- 分页组件 -->
            <div v-if="totalPages > 1" class="border-t border-gray-200 px-6 py-4 bg-gray-50 flex-shrink-0">
                <div class="flex items-center justify-between">
                    <div class="text-sm text-gray-700">
                        显示第 {{ (currentPage - 1) * pageSize + 1 }} - {{ Math.min(currentPage * pageSize, totalDocuments) }} 条，
                        共 {{ totalDocuments }} 条文档
                    </div>
                    <div class="flex items-center space-x-2">
                        <button 
                            @click="changePage(currentPage - 1)"
                            :disabled="currentPage <= 1"
                            :class="[
                                'px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200',
                                currentPage <= 1 
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                            ]"
                        >
                            上一页
                        </button>
                        
                        <div class="flex items-center space-x-1">
                            <button 
                                v-for="page in getVisiblePages()" 
                                :key="page"
                                @click="changePage(page)"
                                :class="[
                                    'px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200',
                                    page === currentPage 
                                        ? 'bg-blue-500 text-white' 
                                        : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                                ]"
                            >
                                {{ page }}
                            </button>
                        </div>
                        
                        <button 
                            @click="changePage(currentPage + 1)"
                            :disabled="currentPage >= totalPages"
                            :class="[
                                'px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200',
                                currentPage >= totalPages 
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                            ]"
                        >
                            下一页
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- 上传弹窗 -->
        <div v-if="showUploadModal" class="fixed inset-0 flex items-center justify-center z-50">
            <!-- 背景遮罩 -->
            <div class="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"></div>
            
            <!-- 弹窗容器 -->
            <div class="relative bg-white rounded-2xl p-8 w-[500px] max-w-[90vw] shadow-2xl">
                <!-- 弹窗头部 -->
                <div class="text-center mb-8">
                    <div class="w-16 h-16 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i class="fas fa-cloud-upload-alt text-white text-2xl"></i>
                    </div>
                    <h3 class="text-2xl font-bold text-gray-900 mb-2">
                        文档上传
                    </h3>
                    <p class="text-gray-600">
                        选择要上传的文档文件（支持多个文档同时上传）
                    </p>
                </div>
                
                <!-- 文件选择区域 -->
                <div class="mb-6">
                    <div 
                        @click="triggerFileInput"
                        @dragover.prevent
                        @drop.prevent="handleFileDrop"
                        class="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-red-400 transition-colors duration-200 cursor-pointer"
                    >
                        <i class="fas fa-cloud-upload-alt text-4xl text-gray-400 mb-4"></i>
                        <p class="text-gray-600 mb-2">
                            点击选择文件或拖拽文件到此处
                        </p>
                                                 <p class="text-sm text-gray-500">
                             支持 PDF、DOCX、TXT 格式，可选择多个文档同时上传，单个文件不超过300MB，整体文件大小不超过700MB
                         </p>
                    </div>
                    
                    <!-- 隐藏的文件输入 -->
                    <input 
                        ref="fileInput"
                        type="file"
                        accept=".pdf,.docx,.txt"
                        @change="handleFileSelect"
                        class="hidden"
                        multiple
                    />
                </div>
                
                <!-- 已选择的文件列表 -->
                <div v-if="selectedFiles.length > 0" class="mb-6">
                    <h4 class="font-medium text-gray-900 mb-3">已选择的文件：</h4>
                    <div class="space-y-2 max-h-32 overflow-y-auto">
                        <div 
                            v-for="(file, index) in selectedFiles" 
                            :key="index"
                            class="flex items-center justify-between bg-gray-50 p-3 rounded-lg"
                        >
                            <div class="flex items-center space-x-3">
                                <i class="fas fa-file text-gray-500"></i>
                                <span class="text-sm text-gray-700">{{ file.name }}</span>
                                <span class="text-xs text-gray-500">({{ formatFileSize(file.size) }})</span>
                            </div>
                            <button 
                                @click="removeFile(index)"
                                class="text-red-500 hover:text-red-700"
                            >
                                <i class="fas fa-times"></i>
                            </button>
                        </div>
                    </div>
                </div>
                
                <!-- 操作按钮 -->
                <div class="flex space-x-4">
                    <button 
                        @click="uploadFiles"
                        :disabled="selectedFiles.length === 0 || isUploading"
                        class="flex-1 bg-red-500 hover:bg-red-600 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center"
                    >
                        <span v-if="isUploading">
                            <i class="fas fa-spinner fa-spin mr-2"></i>上传中...
                        </span>
                        <span v-else>
                            <i class="fas fa-upload mr-2"></i>开始上传
                        </span>
                    </button>
                    <button 
                        @click="closeUploadModal"
                        class="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-3 rounded-lg font-medium transition-colors duration-200"
                    >
                        取消
                    </button>
                </div>
                
                <!-- 关闭按钮 -->
                <button 
                    @click="closeUploadModal"
                    class="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors duration-200"
                >
                    <i class="fas fa-times text-gray-600"></i>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'KnowledgeBase',
         data() {
         return {
             viewMode: 'list', // 'list' 或 'grid'
             sortBy: 'recent',
             showUploadModal: false,
             selectedFiles: [],
             isUploading: false,
             documents: [],
             loading: false,
             // 分页相关
             currentPage: 1,
             pageSize: 10,
             totalPages: 1,
             totalDocuments: 0,
             // 选中的文档ID列表
             selectedDocuments: [],
             // 排序选项映射
             orderByMapping: {
                 'recent': 'create_time',
                 'name': 'name',
                 'size': 'size',
                 'type': 'type'
             }
         };
     },
         mounted() {
         this.loadDocuments();
     },
     computed: {
         // 是否全选
         isAllSelected() {
             const selectableDocs = this.documents.filter(doc => doc.progress === 0);
             return selectableDocs.length > 0 && this.selectedDocuments.length === selectableDocs.length;
         }
     },
     methods: {
        // 处理排序
        handleSort() {
            const sortedDocs = [...this.documents];
            
            switch (this.sortBy) {
                case 'recent':
                    sortedDocs.sort((a, b) => new Date(b.uploadTime) - new Date(a.uploadTime));
                    break;
                case 'name':
                    sortedDocs.sort((a, b) => a.name.localeCompare(b.name));
                    break;
                case 'size':
                    sortedDocs.sort((a, b) => b.size - a.size);
                    break;
                case 'type':
                    sortedDocs.sort((a, b) => a.type.localeCompare(b.type));
                    break;
            }
            
            this.documents = sortedDocs;
        },
        
        // 获取文件类型样式类
        getFileTypeClass(type) {
            const typeClasses = {
                'pdf': 'bg-red-500',
                'docx': 'bg-blue-500',
                'doc': 'bg-blue-600',
                'xlsx': 'bg-green-500',
                'xls': 'bg-green-600',
                'txt': 'bg-gray-500',
                'zip': 'bg-purple-500'
            };
            return typeClasses[type] || 'bg-gray-500';
        },
        
        // 获取文件类型图标
        getFileTypeIcon(type) {
            const typeIcons = {
                'pdf': 'PDF',
                'docx': 'W',
                'doc': 'W',
                'xlsx': 'X',
                'xls': 'X',
                'txt': 'T',
                'zip': 'ZIP'
            };
            return typeIcons[type] || '?';
        },
        
        // 格式化日期
        formatDate(dateString) {
            if (!dateString) return '未知时间';
            
            try {
                // 处理时间戳（毫秒级）
                let timestamp = dateString;
                if (typeof dateString === 'string') {
                    // 如果是字符串，尝试转换为数字
                    timestamp = parseInt(dateString);
                }
                
                if (isNaN(timestamp)) {
                    return '时间格式错误';
                }
                
                const date = new Date(timestamp);
                
                // 检查日期是否有效
                if (isNaN(date.getTime())) {
                    return '无效时间';
                }
                
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                
                return `${year}-${month}-${day} ${hours}:${minutes}`;
            } catch (error) {
                console.warn('日期格式化失败:', error);
                return '时间解析错误';
            }
        },
        
        // 格式化文件大小
        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
        },
        
        // 下载文档
        async downloadDocument(doc) {
            try {
                console.log('开始下载文档:', doc.name);
                console.log('文档ID:', doc.id);
                console.log('环境变量:', process.env.VUE_APP_API_BASE_URL);
                
                // 显示下载中状态
                this.$set(doc, 'downloading', true);
                
                // 构建下载URL
                const downloadUrl = `${process.env.VUE_APP_API_BASE_URL}/api/knowledge/download/${doc.id}?fileName=${encodeURIComponent(doc.name)}`;
                
                console.log('下载URL:', downloadUrl);
                
                // 发起下载请求
                console.log('正在发送下载请求...');
                const response = await fetch(downloadUrl);
                
                console.log('响应状态:', response.status);
                console.log('响应头:', response.headers);
                
                if (!response.ok) {
                    throw new Error(`下载失败: ${response.status} ${response.statusText}`);
                }
                
                // 检查文件大小，决定使用哪种下载方式
                const contentLength = response.headers.get('content-length');
                const fileSize = contentLength ? parseInt(contentLength) : 0;
                const isLargeFile = fileSize > 10 * 1024 * 1024; // 大于10MB的文件
                
                console.log('文件大小:', fileSize, 'bytes, 是否为大文件:', isLargeFile);
                
                if (isLargeFile) {
                    // 大文件使用流式下载，直接触发浏览器下载
                    console.log('检测到大文件，使用流式下载...');
                    await this.streamDownload(response, doc.name);
                } else {
                    // 小文件使用blob方式
                    console.log('小文件，使用blob下载...');
                    await this.blobDownload(response, doc.name);
                }
                
                console.log('文档下载成功:', doc.name);
                
                // 显示成功提示
                if (this.$message) {
                    this.$message.success(`文档 "${doc.name}" 下载成功！`);
                } else {
                    alert(`文档 "${doc.name}" 下载成功！`);
                }
                
            } catch (error) {
                console.error('下载文档失败:', error);
                console.error('错误详情:', {
                    message: error.message,
                    stack: error.stack,
                    name: error.name
                });
                
                // 显示错误提示
                if (this.$message) {
                    this.$message.error(`下载失败: ${error.message}`);
                } else {
                    alert(`下载文档 "${doc.name}" 失败: ${error.message}`);
                }
                
            } finally {
                // 清除下载状态
                this.$set(doc, 'downloading', false);
                console.log('下载状态已清除');
            }
        },
        
        // 流式下载（适用于大文件）
        async streamDownload(response, fileName) {
            try {
                console.log('开始流式下载...');
                
                // 创建ReadableStream
                const reader = response.body.getReader();
                const chunks = [];
                let receivedLength = 0;
                
                // 读取数据流
                while (true) {
                    const { done, value } = await reader.read();
                    
                    if (done) break;
                    
                    chunks.push(value);
                    receivedLength += value.length;
                    
                    // 显示下载进度
                    if (receivedLength % (1024 * 1024) === 0) { // 每1MB显示一次进度
                        console.log(`已下载: ${(receivedLength / (1024 * 1024)).toFixed(1)} MB`);
                    }
                }
                
                console.log('流式下载完成，总大小:', receivedLength, 'bytes');
                
                // 合并所有chunks
                const blob = new Blob(chunks);
                console.log('Blob创建完成，大小:', blob.size, 'bytes');
                
                // 触发下载
                await this.triggerDownload(blob, fileName);
                
            } catch (error) {
                console.error('流式下载失败:', error);
                throw error;
            }
        },
        
        // Blob下载（适用于小文件）
        async blobDownload(response, fileName) {
            try {
                console.log('正在获取文件内容...');
                const blob = await response.blob();
                console.log('文件blob大小:', blob.size, 'bytes');
                console.log('文件blob类型:', blob.type);
                
                // 检查blob是否为空
                if (blob.size === 0) {
                    throw new Error('下载的文件内容为空');
                }
                
                // 触发下载
                await this.triggerDownload(blob, fileName);
                
            } catch (error) {
                console.error('Blob下载失败:', error);
                throw error;
            }
        },
        
        // 触发下载
        async triggerDownload(blob, fileName) {
            try {
                console.log('正在创建下载链接...');
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                
                // 触发下载 - 浏览器会弹出保存对话框让用户选择路径
                console.log('正在触发下载...');
                document.body.appendChild(link);
                link.click();
                
                // 清理
                document.body.removeChild(link);
                
                // 延迟释放URL，确保下载开始
                setTimeout(() => {
                    window.URL.revokeObjectURL(url);
                    console.log('URL已释放');
                }, 1000);
                
            } catch (error) {
                console.error('触发下载失败:', error);
                throw error;
            }
        },
        
                                  // 删除文档
        async deleteDocument(doc) {
            // 检查文件是否正在解析或已解析
            if (doc.progress > 0) {
                alert('解析中或已解析的文件不可删除，如需删除请联系管理员处理！');
                return;
            }
            
            if (confirm(`确定要删除文档 "${doc.name}" 吗？`)) {
                console.log('删除文档:', doc.name);
                
                try {
                    // 调用后端单个删除API
                    const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/api/knowledge/documents/${doc.id}`, {
                        method: 'DELETE',
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    });
                    
                    if (!response.ok) {
                        const errorText = await response.text();
                        throw new Error(`删除失败: ${response.status} ${response.statusText} - ${errorText}`);
                    }
                    
                    const result = await response.json();
                    
                    if (result.code === 200) {
                        alert(`文档 "${doc.name}" 删除成功！`);
                        
                        // 从本地列表中移除已删除的文档
                        this.documents = this.documents.filter(d => d.id !== doc.id);
                        
                        // 如果删除的文档在选中列表中，也要移除
                        const selectedIndex = this.selectedDocuments.indexOf(doc.id);
                        if (selectedIndex > -1) {
                            this.selectedDocuments.splice(selectedIndex, 1);
                        }
                        
                        // 刷新文档列表
                        await this.loadDocuments();
                    } else {
                        throw new Error(result.message || '删除失败，请重试');
                    }
                    
                } catch (error) {
                    console.error('删除文档失败:', error);
                    alert(`删除文档失败: ${error.message}`);
                }
            }
        },
         
         // 切换文档选择状态
         toggleDocumentSelection(docId) {
             const index = this.selectedDocuments.indexOf(docId);
             if (index > -1) {
                 this.selectedDocuments.splice(index, 1);
             } else {
                 this.selectedDocuments.push(docId);
             }
         },
         
         // 全选/取消全选
         toggleSelectAll() {
             if (this.isAllSelected) {
                 this.selectedDocuments = [];
             } else {
                 const selectableDocs = this.documents.filter(doc => doc.progress === 0);
                 this.selectedDocuments = selectableDocs.map(doc => doc.id);
             }
         },
         
         // 批量删除文档
         async batchDeleteDocuments() {
             if (this.selectedDocuments.length === 0) {
                 alert('请选择要删除的文档');
                 return;
             }
             
             if (!confirm(`确定要删除选中的 ${this.selectedDocuments.length} 个文档吗？`)) {
                 return;
             }
             
             try {
                 // 调用后端批量删除API
                 const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/api/knowledge/batch-delete`, {
                     method: 'POST',
                     headers: {
                         'Content-Type': 'application/json'
                     },
                     body: JSON.stringify({
                         documentIds: this.selectedDocuments
                     })
                 });
                 
                 if (!response.ok) {
                     const errorText = await response.text();
                     throw new Error(`删除失败: ${response.status} ${response.statusText} - ${errorText}`);
                 }
                 
                 const result = await response.json();
                 
                 if (result.code === 200) {
                     alert(`批量删除成功！共删除 ${result.deletedCount || this.selectedDocuments.length} 个文档`);
                     
                     // 清空选择列表
                     this.selectedDocuments = [];
                     
                     // 刷新文档列表
                     await this.loadDocuments();
                 } else {
                     throw new Error(result.message || '删除失败，请重试');
                 }
                 
             } catch (error) {
                 console.error('批量删除失败:', error);
                 alert(`批量删除失败: ${error.message}`);
             }
         },
        
        // 打开上传弹窗
        openUploadModal() {
            this.showUploadModal = true;
            this.selectedFiles = [];
        },
        
        // 关闭上传弹窗
        closeUploadModal() {
            this.showUploadModal = false;
            this.selectedFiles = [];
            this.isUploading = false;
        },
        
        // 触发文件选择
        triggerFileInput() {
            this.$refs.fileInput.click();
        },
        
        // 处理文件选择
        handleFileSelect(event) {
            const files = Array.from(event.target.files);
            this.addFiles(files);
        },
        
        // 处理文件拖拽
        handleFileDrop(event) {
            const files = Array.from(event.dataTransfer.files);
            this.addFiles(files);
        },
        
        // 添加文件到选择列表
        addFiles(files) {
            files.forEach(file => {
                // 检查文件类型
                const allowedTypes = ['.pdf', '.docx', '.txt'];
                const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
                if (!allowedTypes.includes(fileExtension)) {
                    alert(`不支持的文件类型: ${fileExtension}，只支持 PDF、DOCX、TXT 格式`);
                    return;
                }
                
                // 检查单个文件大小（限制为300MB，避免单个文件过大）
                if (file.size > 300 * 1024 * 1024) {
                    alert(`文件 ${file.name} 过大，单个文件最大支持300MB`);
                    return;
                }
                
                // 检查整体文件大小（限制为700MB）
                const totalSize = this.selectedFiles.reduce((sum, f) => sum + f.size, 0) + file.size;
                if (totalSize > 700 * 1024 * 1024) {
                    alert(`添加文件 ${file.name} 后，整体文件大小将超过700MB限制`);
                    return;
                }
                
                // 添加到选择列表
                this.selectedFiles.push(file);
            });
        },
        
        // 移除文件
        removeFile(index) {
            this.selectedFiles.splice(index, 1);
        },
        
                 // 上传文件
         async uploadFiles() {
             if (this.selectedFiles.length === 0) {
                 alert('请选择要上传的文件');
                 return;
             }
             
             this.isUploading = true;
             
             try {
                 console.log('开始上传文件:', this.selectedFiles);
                 
                 // 构建FormData对象
                 const formData = new FormData();
                 
                 // 添加所有选中的文件到FormData
                 this.selectedFiles.forEach(file => {
                     formData.append('file', file);
                 });
                 
                 // 构建后端上传URL
                 const uploadUrl = `${process.env.VUE_APP_API_BASE_URL}/api/knowledge/upload`;
                 
                 console.log('后端上传URL:', uploadUrl);
                 console.log('上传文件数量:', this.selectedFiles.length);
                 
                 // 发起上传请求到后端API
                 const response = await fetch(uploadUrl, {
                     method: 'POST',
                     body: formData
                 });
                 
                 console.log('上传响应状态:', response.status);
                 
                 if (!response.ok) {
                     const errorText = await response.text();
                     throw new Error(`上传失败: ${response.status} ${response.statusText} - ${errorText}`);
                 }
                 
                 const result = await response.json();
                 console.log('上传响应结果:', result);
                 
                 // 检查后端返回的状态
                 if (result.code === 200) {
                     // 显示成功提示
                     alert(`文件上传成功！共上传 ${this.selectedFiles.length} 个文件`);
                     
                     // 关闭上传弹窗
                     this.closeUploadModal();
                     
                     // 刷新文档列表
                     await this.loadDocuments();
                 } else {
                     throw new Error(result.message || '上传失败，请重试');
                 }
                 
             } catch (error) {
                 console.error('上传失败:', error);
                 alert(`文件上传失败: ${error.message}`);
             } finally {
                 this.isUploading = false;
             }
         },
        
        // 处理排序
        async handleSort() {
            this.currentPage = 1; // 重置到第一页
            await this.loadDocuments();
        },
        
        // 处理每页条数变化
        async changePageSize(size) {
            if (this.pageSize === size) return; // 如果选择的是当前值，不做任何操作
            this.pageSize = size;
            this.currentPage = 1; // 重置到第一页
            await this.loadDocuments();
        },
        
        // 切换页面
        async changePage(page) {
            if (page >= 1 && page <= this.totalPages) {
                this.currentPage = page;
                await this.loadDocuments();
            }
        },
        
        // 获取可见的页码
        getVisiblePages() {
            const pages = [];
            const maxVisible = 5; // 最多显示5个页码
            
            if (this.totalPages <= maxVisible) {
                // 如果总页数少于等于5，显示所有页码
                for (let i = 1; i <= this.totalPages; i++) {
                    pages.push(i);
                }
            } else {
                // 如果总页数大于5，智能显示页码
                let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
                let end = Math.min(this.totalPages, start + maxVisible - 1);
                
                // 调整起始位置，确保显示maxVisible个页码
                if (end - start + 1 < maxVisible) {
                    start = Math.max(1, end - maxVisible + 1);
                }
                
                for (let i = start; i <= end; i++) {
                    pages.push(i);
                }
            }
            
            return pages;
        },
        
        // 加载文档列表
        async loadDocuments() {
            this.loading = true;
            try {
                // 调试日志
                console.log('发送请求参数:', {
                    page: this.currentPage,
                    pageSize: this.pageSize,
                    sortBy: this.sortBy
                });
                
                // 构建查询参数 - 前端需要将映射值转换为实际字段名
                const orderByMapping = {
                    'recent': 'create_time',
                    'name': 'name',
                    'size': 'size',
                    'type': 'type'
                };
                const apiOrderBy = orderByMapping[this.sortBy] || 'create_time';
                
                const params = new URLSearchParams({
                    page: this.currentPage.toString(),
                    page_size: this.pageSize.toString(),
                    orderby: apiOrderBy, // 传递实际的字段名，如 'create_time'
                    desc: 'true' // 固定为倒序
                });
                
                // 详细的调试日志
                console.log('构建的查询参数:', {
                    page: this.currentPage.toString(),
                    page_size: this.pageSize.toString(),
                    orderby: apiOrderBy,
                    desc: 'true'
                });
                
                const requestUrl = `${process.env.VUE_APP_API_BASE_URL}/api/knowledge/documents?${params.toString()}`;
                console.log('完整的请求URL:', requestUrl);
                console.log('URLSearchParams.toString():', params.toString());
                
                const response = await fetch(requestUrl);
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const data = await response.json();
                
                if (data.code === 200 && data.data && data.data.data) {
                    // 处理返回的文档列表
                    this.documents = (data.data.data.docs || []).map(doc => {
                        // 调试日志
                        console.log('原始文档数据:', doc);
                        
                                                 const mappedDoc = {
                             id: doc.id,
                             name: this.decodeUnicode(doc.name), // 解码Unicode
                             type: doc.suffix || doc.type, // 使用suffix作为文件类型
                             size: doc.size || 0,
                             create_time: doc.create_time, // 直接使用create_time字段
                             chunk_count: doc.chunk_count,
                             progress: doc.progress, // 使用progress字段判断解析状态
                             thumbnail: doc.thumbnail
                         };
                        
                        console.log('映射后的文档数据:', mappedDoc);
                        return mappedDoc;
                    });
                    
                    // 获取总文档数
                    this.totalDocuments = data.data.data.total || 0;
                    this.totalPages = Math.ceil(this.totalDocuments / this.pageSize);
                    
                    console.log('文档列表加载成功:', this.documents.length, '个文档');
                } else {
                    console.error('获取文档列表失败:', data.message || '未知错误');
                    this.documents = [];
                    this.totalDocuments = 0;
                    this.totalPages = 1;
                }
                
            } catch (error) {
                console.error('加载文档列表时发生错误:', error);
                this.documents = [];
                this.totalDocuments = 0;
                this.totalPages = 1;
            } finally {
                this.loading = false;
            }
        },
        
        // 解码Unicode字符串
        decodeUnicode(str) {
            if (!str) return '';
            try {
                // 处理Unicode编码的中文字符
                return str.replace(/\\u([0-9a-fA-F]{4})/g, (match, hex) => {
                    return String.fromCharCode(parseInt(hex, 16));
                });
            } catch (e) {
                console.warn('Unicode解码失败:', e);
                return str;
            }
        }
    }
};
</script>

<style scoped>
.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

/* 自定义滚动条样式 */
.overflow-y-auto::-webkit-scrollbar {
    width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}
</style>
